import {
  useEffect,
  useState,
  useMemo,
  memo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ROUTE from 'src/utils/routeConstants';
import Loader from 'src/common/Loader';
import { getVehicleList } from 'src/redux/vehicleSlice';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, vehicleTableHeaders } from 'src/utils/tableHeaders';
import { getMileageType, getSelectedSiteFilterValue } from 'src/utils/getInitials';
import CustomPagination from 'src/common/CustomPagination';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

/* ---------- Mobile Card ---------- */
const MobileCardView = memo(({ vehicle }) => (
  <Card
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'divider',
      cursor: 'pointer'
    }}
  >
    <Typography fontWeight={700}>
      {vehicle.registration_num}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {vehicle.model || '-'}
    </Typography>

    <Box mt={1}>
      <Chip
        label={vehicle.active ? 'Active' : 'Inactive'}
        color={vehicle.active ? 'success' : 'error'}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 600 }}
      />
    </Box>
  </Card>
));

MobileCardView.propTypes = {
  vehicle: PropTypes.object.isRequired,
};

/* ---------- Main Table ---------- */
const VehicleListTable = ({ refreshData, searchText, siteFilter }) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const { vehicleList = [], loading, totalCount = 0 } = useSelector((state) => state.vehicles);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('registration_num');
  const vehicleAlignById = useMemo(() => getAlignByColumnId(vehicleTableHeaders), []);
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(getVehicleList({
      limit,
      pageNo: 1,
      params: { registration_num: searchText, site: selectedSite }
    }));
  }, [dispatch, limit, refreshData, searchText, selectedSite]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getVehicleList({
      limit,
      pageNo: newPage + 1,
      params: { registration_num: searchText, site: selectedSite }
    }));
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const viewVehicleInfo = (id) => {
    history.push(`${ROUTE.vehicles}/${id}`);
  };

  const displayData = vehicleList
    ?.slice()
    .sort(getComparator(order, orderBy)) || [];

  if (loading) return <Loader />;

  /* ---------- MOBILE ---------- */
  if (isMobile) {
    return (
      <Box>
        {displayData.map((vehicle) => (
          <MobileCardView
            key={vehicle.id}
            vehicle={vehicle}
            onViewClick={viewVehicleInfo}
          />
        ))}
        <CustomPagination
          page={page}
          rowsPerPage={limit}
          count={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
        />
      </Box>
    );
  }

  /* ---------- DESKTOP ---------- */
  return (
    <Card
      sx={{
        borderRadius: 2,
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          minHeight: 0,
          flex: 1,
          overflow: 'auto',
          '& .MuiTableCell-stickyHeader': {
            backgroundColor: '#F9FAFB',
            borderBottom: `1px solid ${theme.palette.divider}`,
            zIndex: 2
          },
          '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
            position: 'sticky',
            right: 0,
            zIndex: 3,
            backgroundColor: '#F9FAFB',
            boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        <Box sx={{ minWidth: isTablet ? 800 : 1050 }}>
          <Table stickyHeader>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              headCells={vehicleTableHeaders}
              onRequestSort={handleRequestSort}
              sx={{
                backgroundColor: '#F9FAFB',
                borderBottom: `1px solid ${theme.palette.divider}`
              }}
            />
            <TableBody>
              {displayData.length > 0 ? (
                displayData.map((vehicle) => (
                  <TableRow
                    hover
                    key={vehicle.id}
                    sx={{
                      '&:hover': { backgroundColor: '#F9FAFB' }
                    }}
                  >
                    <TableCell align={vehicleAlignById.registration_num}>{vehicle.registration_num}</TableCell>

                    <TableCell
                      align={vehicleAlignById.model}
                      sx={{
                        color: vehicle.model ? 'text.primary' : 'text.disabled'
                      }}
                    >
                      {vehicle.model || '-'}
                    </TableCell>

                    <TableCell
                      align={vehicleAlignById.vehicle_group}
                      sx={{
                        color: vehicle.group_name
                          ? 'text.primary'
                          : 'text.disabled'
                      }}
                    >
                      {vehicle.group_name || '-'}
                    </TableCell>

                    <TableCell
                      align={vehicleAlignById.site_name}
                      sx={{
                        color: vehicle.site_name
                          ? 'text.primary'
                          : 'text.disabled'
                      }}
                    >
                      {vehicle.site_name || '-'}
                    </TableCell>

                    <TableCell
                      align={vehicleAlignById.asset_code}
                      sx={{
                        color: vehicle.asset_code
                          ? 'text.primary'
                          : 'text.disabled'
                      }}
                    >
                      {vehicle.asset_code || '-'}
                    </TableCell>

                    <TableCell align={vehicleAlignById.type}>{getMileageType(vehicle.type)}</TableCell>

                    <TableCell align={vehicleAlignById.active}>
                      <Chip
                        label={vehicle.active ? 'Active' : 'Inactive'}
                        color={vehicle.active ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>

                    {/* ACTION */}
                    <TableCell
                      align={vehicleAlignById.action}
                      sx={{
                        position: 'sticky',
                        right: 0,
                        zIndex: 1,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      <Tooltip title="View Details">
                        <IconButton
                          size="medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewVehicleInfo(vehicle.id);
                          }}
                          aria-label="View details"
                          sx={getTableActionIconButtonSx(theme)}
                        >
                          <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={vehicleTableHeaders.length} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      No Vehicles Found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      <Box sx={{ p: 1, flexShrink: 0, bgcolor: '#FFFFFF' }}>
        <CustomPagination
          page={page}
          rowsPerPage={limit}
          count={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
        />
      </Box>
    </Card>
  );
};

VehicleListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default VehicleListTable;
